import React, { useEffect, useState } from "react";
import { Button, Container, Typography, IconButton, CircularProgress, Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions } from "@mui/material";
import { Link } from "react-router-dom";
import { TableComponent } from "../../components/Table";
import { ModalComponent } from "../../components/Modal";
import { providers } from "../../api/providers";
import { Provider } from "./interface/provider.interface";
import { useNotification } from "../../context/notification.context";
import { GridColDef } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import FileDownload from "@mui/icons-material/FileDownload";
import { utils, writeFileXLSX } from "xlsx";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";

export const ProvidersPage: React.FC<{}> = () => {
  const [allProviders, setAllProviders] = useState<Provider[] | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [idToDelete, setIdToDelete] = useState<number | null>(null);
  const { getError, getSuccess } = useNotification();

  // Balance export state
  const [fechaHasta, setFechaHasta] = useState<Dayjs | null>(null);
  const [isExportingBalance, setIsExportingBalance] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await providers.getAll();
        const providersData = await response.data.map((provider: Provider) => {
          return {
            id: provider.id,
            nombre: provider.nombre,
            tel: provider.tel,
            domicilio: provider.domicilio,
            mail: provider.mail,
            comentarios: provider.comentarios
          };
        });
        setAllProviders(providersData);
      } catch (error) {
        getError(
          `Ocurrio un error inesperado al cargar los proveedores: ${error}`
        );
      }
    };
    fetchData();
  }, [getError]);

  // Function to format numbers with custom separators
  const formatNumber = (number: number) => {
    return number.toLocaleString('es', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    });
  };

  const handleExportBalance = async () => {
    try {
      if (!fechaHasta || !fechaHasta.isValid()) {
        getError("Debe seleccionar una fecha límite para el balance");
        return;
      }
      setIsExportingBalance(true);

      const fechaStr = fechaHasta.format("YYYY-MM-DD");

      const response = await providers.getBalance(fechaStr);
      const balanceData = response.data;

      if (balanceData.length === 0) {
        getError("No se encontraron proveedores con saldo adeudado para la fecha seleccionada");
        setIsExportingBalance(false);
        return;
      }

      // Preparar datos para el Excel
      const excelData = balanceData.map((item: any) => ({
        "Proveedor": item.nombre,
        "Teléfono": item.tel,
        "E-mail": item.mail,
        "Total ARS": formatNumber(item.total_ars),
        "Pagado ARS": formatNumber(item.pagado_ars),
        "Saldo ARS": formatNumber(item.saldo_ars),
        "Total USD": formatNumber(item.total_usd),
        "Pagado USD": formatNumber(item.pagado_usd),
        "Saldo USD": formatNumber(item.saldo_usd),
      }));

      // Calcular totales
      const totals = balanceData.reduce((acc: any, item: any) => ({
        total_ars: acc.total_ars + item.total_ars,
        pagado_ars: acc.pagado_ars + item.pagado_ars,
        saldo_ars: acc.saldo_ars + item.saldo_ars,
        total_usd: acc.total_usd + item.total_usd,
        pagado_usd: acc.pagado_usd + item.pagado_usd,
        saldo_usd: acc.saldo_usd + item.saldo_usd,
      }), { total_ars: 0, pagado_ars: 0, saldo_ars: 0, total_usd: 0, pagado_usd: 0, saldo_usd: 0 });

      // Agregar fila de totales
      excelData.push({
        "Proveedor": "TOTALES",
        "Teléfono": "",
        "E-mail": "",
        "Total ARS": formatNumber(totals.total_ars),
        "Pagado ARS": formatNumber(totals.pagado_ars),
        "Saldo ARS": formatNumber(totals.saldo_ars),
        "Total USD": formatNumber(totals.total_usd),
        "Pagado USD": formatNumber(totals.pagado_usd),
        "Saldo USD": formatNumber(totals.saldo_usd),
      });

      // Crear y descargar Excel
      const wb = utils.book_new();
      const ws = utils.json_to_sheet(excelData);

      // Ajustar ancho de columnas
      ws['!cols'] = [
        { wch: 35 }, // Proveedor
        { wch: 18 }, // Teléfono
        { wch: 25 }, // E-mail
        { wch: 15 }, // Total ARS
        { wch: 15 }, // Pagado ARS
        { wch: 15 }, // Saldo ARS
        { wch: 15 }, // Total USD
        { wch: 15 }, // Pagado USD
        { wch: 15 }, // Saldo USD
      ];

      utils.book_append_sheet(wb, ws, "Balance Proveedores");

      const fechaFormateada = fechaHasta.format("YYYYMMDD");
      writeFileXLSX(wb, `Balance_Proveedores_hasta_${fechaFormateada}.xlsx`);

      getSuccess(`Balance exportado exitosamente (${balanceData.length} proveedores con saldo)`);
      setIsExportingBalance(false);
    } catch (error) {
      getError(`Error al exportar el balance: ${error}`);
      setIsExportingBalance(false);
    }
  };

  const handleAddProvider = async (newProvider: Provider) => {
    try {
      setIsLoading(true);
      if(Object.keys(newProvider).length > 0 && newProvider.nombre !== ""){
        const response = await providers.create(newProvider);
        setAllProviders((prevProviders) => {
          if (prevProviders) {
            return [...prevProviders, response.data];
          }
          return [response.data];
        });
        getSuccess("Proveedor agregado correctamente");
        setIsModalOpen(false);
      }else{
        getError("El nombre es requerido");
      }
      setIsLoading(false);
    } catch (error) {
      getError("Error al agregar el proveedor: " + error);
    }
  };

  const handleEditProvider = async (editedProvider: Provider) => {
    try {
      setIsLoading(true);
      if (selectedProvider && selectedProvider.id !== undefined) {
        const response = await providers.update(
          selectedProvider.id,
          editedProvider
        );
        setAllProviders((prevProviders) => {
          if (prevProviders) {
            return prevProviders.map((provider) => {
              if (provider.id === response.data.id) {
                return response.data;
              }
              return provider;
            });
          }
          return null;
        });
        getSuccess("Proveedor actualizado correctamente");
        setIsModalOpen(false);
        setIsLoading(false);
      }
    } catch (error) {
      getError("Error al actualizar el proveedor");
    }
  };

  const handleModalOpen = () => {
    setSelectedProvider(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleEditModalOpen = (provider: Provider) => {
    setSelectedProvider(provider);
    setIsModalOpen(true);
  };

  const handleEditModalClose = () => {
    setSelectedProvider(null);
    setIsModalOpen(false);
  };

  const handleDeleteProvider = async (id: number) => {
    try {
      setIsLoading(true);
      await providers.delete(id);   
      getSuccess("Proveedor eliminado correctamente");  
      setIsLoading(false);
      setDialogOpen(false);
    } catch (error) {
      getError(`Error al eliminar el elemento: ${error}`);
    }
  };

  const handleDialogOpen = (id: number) => {
    setIdToDelete(id);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  if (isLoading) {
    return <CircularProgress sx={{
      position: 'absolute',
      top: '50%',
      left: '50%'}}/>;
  }

  const columns: GridColDef[] = [
    {
      field: "nombre",
      headerName: "Nombre",
      align: "left",
      headerAlign: "left",
      minWidth: 400,
      disableColumnMenu: true
    },
    {
      field: "tel",
      headerName: "Teléfono",
      align: "left",
      headerAlign: "left",
      minWidth: 200,
      disableColumnMenu: true
    },
    {
      field: "mail",
      headerName: "E-mail",
      align: "left",
      headerAlign: "left",
      minWidth: 200,
      disableColumnMenu: true
    },
    {
      field: "comentarios",
      headerName: "Comentarios",
      align: "left",
      headerAlign: "left",
      minWidth: 300,
      disableColumnMenu: true
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ pt: 5}}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        <IconButton aria-label="routeback" size="small" component={Link} to="/">
          <ArrowBackIos fontSize="small" />
        </IconButton>        
        Proveedores
      </Typography>        

      <div style={{float: "right", display: "flex", gap: "12px", alignItems: "center"}}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
          <DatePicker
            label="Adeudado hasta"
            format="DD/MM/YYYY"
            value={fechaHasta}
            onChange={(value) => setFechaHasta(value)}
            slotProps={{ textField: { size: "small", sx: { width: 200, mt: 2, mb: 2 } } }}
          />
        </LocalizationProvider>
        <Button
          size="medium"
          variant="contained"
          color="success"
          sx={{ mt: 2, mb: 2 }}
          startIcon={isExportingBalance ? <CircularProgress size={20} color="inherit" /> : <FileDownload />}
          onClick={handleExportBalance}
          disabled={isExportingBalance}
        >
          {isExportingBalance ? "Exportando..." : "Exportar Balance"}
        </Button>
        <Button
          size="medium"
          variant="contained"
          sx={{ mt: 2, mb: 2 }}
          startIcon={<AddIcon />}
          onClick={handleModalOpen}
        >
          Agregar Proveedor
        </Button>
      </div>

      {allProviders ? (
        <TableComponent
          columnData={columns}
          rowData={allProviders}
          actions={true}
          actionsType={"providers"}
          onEdit={handleEditModalOpen}
          onDelete={handleDialogOpen}
          exportTitle={"Listado de Proveedores"}
        />
      ) : (
        <Typography variant="body1">Cargando proveedores...</Typography>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Eliminar Proveedor
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Está seguro que desea eliminar el proveedor?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancelar</Button>
          <Button onClick={() => {handleDeleteProvider((idToDelete != null)?idToDelete:0)}} autoFocus>
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
     
      <ModalComponent
        open={isModalOpen}
        onClose={handleModalClose}
        onSave={selectedProvider ? handleEditProvider : handleAddProvider}
        onCancel={handleEditModalClose}
        title={selectedProvider ? "Editar Proveedor" : "Agregar Proveedor"}
        fields={[
          { label: "Nombre", key: "nombre", required: true },
          { label: "Domicilio", key: "domicilio" },
          { label: "Teléfono", key: "tel" },
          { label: "E-mail", key: "mail", required: true },
          { label: "Comentarios", key: "comentarios" },
        ]}
        initialData={selectedProvider}
      />
    </Container>
  );
};
